<template>
    <div data-cy="pizza-toppings" class="pizza-toppings">
        <div v-if="isHandTossed || isStuffedCrust || isBakery" class="mb-16" :class="{ 'd-none': sauceCups?.length < 2 }">
            <ion-card-title class="mx-8 mb-8 ion-text-center bold">Sauce Cup?</ion-card-title>
            <radiolist :options="sauceCups" keyName="name" dense v-model="localDressing" data-cy="choose-sauce-cup" defaultFirst />
        </div>
        <ion-card-title class="mx-8 mb-8 ion-text-center bold" v-if="initialItem.isPizza">
            <span v-if="selectedRecipe?.isCyo && !selectedRecipe?.isHandTossed">Load Up Your Pizza With Toppings Piled High!</span>
            <span v-else-if="selectedRecipe?.isHandTossed"> This Pizza Comes Loaded With: </span>
            <span v-else> Load Up Your Pizza With Edge to Edge<sup>&reg;</sup> Toppings </span>
        </ion-card-title>
        <ion-card-title class="mx-8 mb-8 ion-text-center bold" v-if="initialItem.isCalzato || initialItem.isSub">Customize Your {{ initialItem.isCalzato ? 'Calzato' : 'Sub' }}</ion-card-title>
        <ion-row class="ion-justify-content-center" v-if="initialItem?.isHalfTopping">
            <ion-col size="12" size-sm="10" class="half-toggle-box my-8">
                <ion-item lines="none">
                    <ion-label>Make This a Half Signature Pizza</ion-label>
                    <ion-toggle mode="ios" color="success" :checked="isHalfSig" @ionChange="sigToggle($event.target.checked)" data-cy="toggle-half-signature" />
                </ion-item>
            </ion-col>
        </ion-row>

        <defaultToppings
            v-if="!initialItem.isCustomPizza"
            :availableTopping="availableTopping"
            v-model:defaultToppings="localDefaultToppings"
            :hideSplitOpts="initialItem.isSub || initialItem.isCalzato || (!initialItem.isCustomPizza && initialItem.isClassic && !!localToppings)"
            :toppingCount="toppingCount"
        />
        <!-- Custom whole topping selection -->
        <proteinVeggieSelection
            v-if="(initialItem.isCustomPizza || initialItem.isBakery) && !isHalfSig && localToppings"
            :availableTopping="availableTopping"
            v-model:toppings="wholeToppings"
            :hideSplitOpts="initialItem.isSub || initialItem.isCalzato || initialItem.isBakery"
        />

        <!-- Custom half-half signature selection -->
        <div v-else-if="initialItem.isPizza && initialItem.isCustomPizza && isHalfSig" data-cy="half-signature-section">
            <ion-segment mode="md" :value="currentSide" @ionChange="sideToggle($event.target.value)">
                <ion-segment-button :value="PizzaSideEnum.left" data-cy="button-select-left"> Left Side </ion-segment-button>
                <ion-segment-button :value="PizzaSideEnum.right" data-cy="button-select-right"> Right Side </ion-segment-button>
            </ion-segment>
            <radiolist
                v-if="currentSide == PizzaSideEnum.left"
                :options="signaturePizzas"
                keyName="name"
                selectedKey="nameAndDescription"
                dense
                v-model="leftSideSig"
                left
                @update="openModal(PizzaSideEnum.left, $event)"
                data-cy="left-signature-list"
            />
            <radiolist
                v-else
                :options="signaturePizzas"
                keyName="name"
                selectedKey="nameAndDescription"
                dense
                v-model="rightSideSig"
                right
                @update="openModal(PizzaSideEnum.right, $event)"
                data-cy="right-signature-list"
            />
        </div>

        <!-- More Topping modal button -->
        <div v-else-if="!initialItem.isCustomPizza && !initialItem.isClassic && localToppings" class="more-toppings-wrapper">
            <toppingModal
                btnName="More Toppings"
                showToppingPreview
                @update="wholeToppings = $event"
                :hideSplitOpts="initialItem.isSub || initialItem.isCalzato"
                :availableTopping="availableTopping"
                v-model:toppings="wholeToppings"
                :parentToppingCount="parentToppingCount"
                :initialItem="initialItem"
            />
        </div>

        <!-- Two sided pizza modals -->
        <toppingModal
            v-if="customSideModal && currentSide == PizzaSideEnum.left"
            @close="customSideModalDelayClose"
            @update="updateHalfModalTops(PizzaSideEnum.left, $event)"
            justModal
            hideSplitOpts
            :availableTopping="availableTopping"
            :parentToppingCount="0"
            v-model:toppings="leftToppings"
        />
        <toppingModal
            v-if="customSideModal && currentSide == PizzaSideEnum.right"
            @close="customSideModalDelayClose"
            @update="updateHalfModalTops(PizzaSideEnum.right, $event)"
            justModal
            hideSplitOpts
            :availableTopping="availableTopping"
            :parentToppingCount="0"
            v-model:toppings="rightToppings"
        />
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, nextTick, watch } from 'vue';
import { IonItem, IonCol, IonToggle, IonLabel, IonCardTitle, IonSegment, IonSegmentButton } from '@ionic/vue';
import defaultToppings from '@/components/menu/toppings/defaultToppings.vue';
import baseToppings, { passThroughBindings } from '@/components/menu/toppings/baseToppings.vue';
import proteinVeggieSelection from '@/components/menu/toppings/proteinVeggieSelection.vue';
import radiolist from '@/components/inputs/radiolist.vue';
import { useStore } from 'vuex';
import toppingModal from '@/components/menu/toppings/toppingModal.vue';
import { PizzaSideEnum } from '@/enums/PizzaSideEnum';
import { menuActionScroller } from '@/services/pageService';
import { lineItemMapper } from '@/services/orderService';
import { AvailableTopping } from '@/models/menuModels';

export default defineComponent({
    extends: baseToppings,
    components: {
        defaultToppings,
        proteinVeggieSelection,
        IonItem,
        IonToggle,
        IonLabel,
        IonCardTitle,
        radiolist,
        IonCol,
        IonSegment,
        IonSegmentButton,
        toppingModal,
    },
    setup(props, { emit }) {
        const store = useStore();
        const { localToppings, localDefaultToppings, localTwoSidedItem, localDressing } = passThroughBindings(props, emit);
        const wholeToppings = ref();
        const toppingCount = ref();

        // Two sided custom pizza variables
        const signaturePizzas = computed(() =>
            store.getters.getLocationMenu
                ?.find((c) => c.name === 'Pizza')
                ?.items?.filter((i) => i.recipes?.some((r) => r.recipeName == props.selectedRecipe?.recipeName) && !i.name.toLowerCase().includes('block'))
                ?.sort((a, b) => a.isCustomPizza - b.isCustomPizza)
        );
        const isStuffedCrust = computed(() => props.selectedRecipe?.isStuffedCrust);
        const isHandTossed = computed(() => props.selectedRecipe?.isHandTossed);
        const isBakery = computed(() => props.selectedRecipe?.isBakery);
        const isHalfSig = ref(false);
        const currentSide = ref(PizzaSideEnum.left);
        const leftSideSig = ref();
        const rightSideSig = ref();
        const leftToppings = ref();
        const rightToppings = ref();
        const customSideModal = ref();

        nextTick(() => {
            // Initial selection
            localToppings.value = props.availableTopping?.toppings;
            wholeToppings.value = props.twoSidedItem?.toppings ?? props.availableTopping?.deepUnselectedCopyToppings;
            localDefaultToppings.value = props.availableTopping?.deepDefaultCopyToppings;

            if (props.dressing) {
                localDressing.value = props.sauceCups?.find((s) => s.name == props.dressing?.name);
            }

            // Set Custom pizza variables
            leftToppings.value = props.availableTopping?.deepUnselectedCopyToppings;
            rightToppings.value = props.availableTopping?.deepUnselectedCopyToppings2;
            if (props.twoSidedItem && props.twoSidedItem?.id2) {
                isHalfSig.value = true;
                leftSideSig.value = signaturePizzas.value.find((s) => s.itemExternalId === props.twoSidedItem?.id);
                rightSideSig.value = signaturePizzas.value.find((s) => s.itemExternalId === props.twoSidedItem?.id2);
                emitCustomTwoHalfItem();
            }
        });

        watch(wholeToppings, (w) => (localToppings.value = w));

        //#region Two Half Custom Pizza
        const customSideModalDelayClose = () => setTimeout(() => (customSideModal.value = false), 150);
        const openModal = (side, type) => (customSideModal.value = type.isCustomPizza);
        const updateHalfModalTops = (side, toppings) => {
            PizzaSideEnum.left === side ? (leftToppings.value = toppings) : (rightToppings.value = toppings);
            emitCustomTwoHalfItem();
        };
        const sigToggle = (isChecked) => {
            isHalfSig.value = isChecked;
            menuActionScroller();
            emitCustomTwoHalfItem();
        };
        const sideToggle = (isChecked) => {
            currentSide.value = isChecked;
            menuActionScroller();
        };

        /** Creates LineItem with two menuitems when customizing a two sided pizza */
        const emitCustomTwoHalfItem = () => {
            const block = isHalfSig.value && (!leftSideSig.value || !rightSideSig.value);
            emit('blockSubmit', block);
            if (!isHalfSig.value) return (localTwoSidedItem.value = undefined);

            // force toppings placement for custom half and half signatures
            if (leftSideSig.value?.isCustomPizza) {
                leftToppings.value.forEach((topping) => (topping.placement = PizzaSideEnum.left));
            }
            if (rightSideSig.value?.isCustomPizza) {
                rightToppings.value.forEach((topping) => (topping.placement = PizzaSideEnum.right));
            }

            localTwoSidedItem.value = lineItemMapper(
                leftSideSig.value,
                [], //? these get set in menuItemPage.alignTwoSidedItem()
                1,
                //@ts-ignore
                new AvailableTopping({
                    toppings: leftSideSig.value?.isCustomPizza ? leftToppings.value : props.availableTopping?.deepUnselectedCopyToppings,
                    updatingSelectedToppings: (leftSideSig.value?.isCustomPizza ? leftToppings.value : props.availableTopping?.deepUnselectedCopyToppings)?.filter((t) => t.selected),
                    dressings: !localDressing.value ? [] : localDressing.value instanceof Array ? localDressing.value : [localDressing.value],
                    toppings2: rightSideSig.value?.isCustomPizza ? rightToppings.value : props.availableTopping?.deepUnselectedCopyToppings2,
                    updatingSelectedToppings2: (rightSideSig.value?.isCustomPizza ? rightToppings.value : props.availableTopping?.deepUnselectedCopyToppings2)?.filter((t) => t.selected),
                    flavors2: [],
                    dressings2: !localDressing.value ? [] : localDressing.value instanceof Array ? localDressing.value : [localDressing.value],
                    //dressings2: [],
                    sauces2: [],
                }),
                props.selectedRecipe,
                props.selectedSize,
                rightSideSig.value
            );
            return;
        };

        watch(leftToppings, () => {
            const isLeftSideCYO = leftSideSig.value?.isCustomPizza ?? false;
            const leftHasToppings = leftToppings.value.filter((c) => c.selected).length >= 1;
            const isValidHalf = !isLeftSideCYO || (isLeftSideCYO && leftHasToppings);
            if (!isValidHalf) {
                leftSideSig.value = undefined;
            }
        });
        watch(rightToppings, () => {
            const isRightSideCYO = rightSideSig.value?.isCustomPizza ?? false;
            const rightHasToppings = rightToppings.value.filter((c) => c.selected).length >= 1;
            const isValidHalf = !isRightSideCYO || (isRightSideCYO && rightHasToppings);
            if (!isValidHalf) {
                rightSideSig.value = undefined;
            }
        });

        watch([leftSideSig, rightSideSig], () => {
            emitCustomTwoHalfItem();
            // Problem: Updating sideSig and Adding to Cart immediatly was causing the sideSig selection to fall off.
            // Temp Fix: Blocking submit for 1 second after selection.
            // TODO: Find and fix the root cause. & remove bandaid fix.
            emit('blockSubmit', true);
            const block = isHalfSig.value && (!leftSideSig.value || !rightSideSig.value);
            delay(1000).then(() => emit('blockSubmit', block));
        });

        watch(signaturePizzas, (sigList) => {
            if (leftSideSig.value && !sigList.some((s) => s.itemId === leftSideSig.value.itemId)) leftSideSig.value = undefined;
            if (rightSideSig.value && !sigList.some((s) => s.itemId === rightSideSig.value.itemId)) rightSideSig.value = undefined;
        });
        //#endregion

        const delay = (time) => {
            return new Promise((resolve) => setTimeout(resolve, time));
        };

        return {
            isHandTossed,
            isStuffedCrust,
            isBakery,
            isHalfSig,
            currentSide,
            signaturePizzas,
            PizzaSideEnum,
            customSideModal,
            toppingCount,

            leftSideSig,
            rightSideSig,

            localToppings,
            localDefaultToppings,
            wholeToppings,
            leftToppings,
            rightToppings,
            localDressing,

            updateHalfModalTops,
            sigToggle,
            sideToggle,
            openModal,
            customSideModalDelayClose,
        };
    },
});
</script>

<style scoped>
ion-item::part(native) {
    padding-left: 0;
}

ion-segment-button {
    --margin-bottom: 0;
}

.more-toppings {
    position: absolute;
    bottom: 0;
    left: 0;
}

.more-toppings-wrapper {
    margin-top: 48px;
}

.half-toggle-box {
    border: 1px solid var(--ion-color-light);
    padding: 0 20px;
    border-radius: 4px;
    box-shadow: rgb(0 0 0 / 5%) 0px 3px 1px -2px, rgb(0 0 0 / 3.5%) 0px 2px 2px 0px, rgb(0 0 0 / 3%) 0px 2px 2px 0px;
}
</style>
